import { ANALYTICS_JLDATA_UPDATED } from "../utils/events";
import merge from "deepmerge";
import { analyticsLogger } from "@/utils/clientLogger";

export const dispatchLegacyAnalyticsEvent = (
  eventName: string,
  data = {},
  { timeout = 10000, predecessorEventName = ANALYTICS_JLDATA_UPDATED } = {},
) => {
  const event = new CustomEvent("dataStateChange", {
    detail: { action: eventName, ...data },
  });

  const deferredDispatchPromise = new Promise((resolve, reject) => {
    // it's reassigned after initialized.
    // eslint-disable-next-line
    let wait: NodeJS.Timeout | undefined;

    const listener = () => {
      analyticsLogger.info(
        `analytics.ts: Dispatched dataStateChange ${eventName} containing ${JSON.stringify(data)}`,
      );
      document.body.dispatchEvent(event);
      document.body.removeEventListener(predecessorEventName, listener);
      clearTimeout(wait);
      resolve(event);
    };

    document.body.addEventListener(predecessorEventName, listener, {
      once: true,
    });

    wait = setTimeout(() => {
      analyticsLogger.info(
        `analytics.ts: Timed out waiting for ${predecessorEventName}`,
      );
      document.body.removeEventListener(predecessorEventName, listener);
      reject();
    }, timeout);
  });

  return deferredDispatchPromise.catch((error) => {
    analyticsLogger.warn(
      `WARNING: expected "${eventName}" to trigger but it did not.`,
      error,
    );
  });
};

export const dispatchAnalyticsEvent = (
  action: string,
  payload?: any, // eslint-disable-line @typescript-eslint/no-explicit-any
  jlData?: {
    [key: string]: unknown;
  },
) => {
  if (window?.Bootstrapper?.dataStateChangeReady) {
    dispatchEvent(action, payload, jlData);
  } else {
    document.body.addEventListener(
      "EnsightenReady",
      () => dispatchEvent(action, payload, jlData),
      { once: true },
    );
  }
};

const dispatchEvent = (
  action: string,
  payload: any, // eslint-disable-line @typescript-eslint/no-explicit-any
  jlData?: {
    [key: string]: unknown;
  },
) => {
  if (jlData) {
    setAnalyticsData(jlData);
  }

  analyticsLogger.info(
    `analytics.ts: dispatching dataStateChange with action: ${action}${payload ? ` and payload ${JSON.stringify(payload)}` : ""}`,
  );

  document.body.dispatchEvent(
    new CustomEvent("dataStateChange", {
      detail: {
        action,
        ...payload,
      },
    }),
  );
};

const setAnalyticsData = (payload = {}) => {
  const currentJLData = window.jlData || {};
  const overwriteMerge = (
    _destination: any, // eslint-disable-line @typescript-eslint/no-explicit-any
    source: any, // eslint-disable-line @typescript-eslint/no-explicit-any
  ) => source;

  const options = {
    customMerge: (key: string) => {
      if (key === "quickView") {
        return overwriteMerge;
      }
      return;
    },
  };

  const newJLData = merge(currentJLData, payload, options);
  window.jlData = newJLData;
};
